import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, StatusPill, Table, useToast,
} from '../../../design-system';
import { dayTime } from '../../../lib/tallyBooks';
import '../../styles/TallyPanel.css';

// The sync, while it is happening.
//
// ⚠️ SYNCING IS MANUAL, WHICH IS WHY THIS SCREEN EXISTS. When a cron ran the
// syncs overnight nobody was watching and "last synced 9 hours ago" was the
// whole story anybody needed. A person who has just pressed the button is
// standing there, and their three questions — is it doing anything, what is
// left, and if it failed then why — were answerable only from the database.
//
// ⚠️ THE MACHINE IS REPORTED SEPARATELY FROM THE SYNC, and that separation is
// the point. "Nothing is happening" has three different causes with three
// different people to ring: the PC is off, the PC is on but Tally is closed, or
// Tally answered and refused. This module says so everywhere else; the one
// screen somebody stares at while waiting should say it loudest.

// Poll only while there is something to watch. A finished queue that keeps
// refetching every two seconds is a screen left open on somebody's second
// monitor hammering the API all afternoon.
const POLL_MS = 2500;

const JOB_PILL = {
  queued: { variant: 'default', label: 'Waiting' },
  claimed: { variant: 'info', label: 'Running' },
  succeeded: { variant: 'success', label: 'Done' },
  failed: { variant: 'danger', label: 'Failed' },
  expired: { variant: 'danger', label: 'Gave up' },
};

const RUN_PILL = {
  running: { variant: 'info', label: 'Running' },
  succeeded: { variant: 'success', label: 'Succeeded' },
  partial: { variant: 'warning', label: 'Partly done' },
  failed: { variant: 'danger', label: 'Failed' },
};

/** "in 4s", "2m ago" — a relative time is what a person watching actually reads. */
function ago(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function TallySyncActivity({ connection, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/api/tally/connections/${connection.id}/activity`);
      setData(res.data);
      return res.data;
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not read the sync status');
      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.id]);

  useEffect(() => { load(); }, [load]);

  // ⚠️ POLLS ONLY WHILE WORK IS OUTSTANDING, and re-arms from the answer rather
  // than on a fixed interval — so the last poll after a queue drains is the one
  // that stops it. A setInterval would keep firing against a finished sync for
  // as long as the tab stayed open.
  const busy = !!data && (data.running || data.queue.length > 0);
  useEffect(() => {
    clearTimeout(timer.current);
    if (!busy) return undefined;
    timer.current = setTimeout(() => { load(); }, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [busy, data, load]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const syncNow = async () => {
    setStarting(true);
    try {
      await apiClient.post(`/api/tally/connections/${connection.id}/sync`);
      toast.success('Sync queued');
      await load();
      onChanged?.();
    } catch (e) {
      const body = e.response?.data;
      // ⚠️ THE "ALREADY RUNNING" REFUSAL IS NOT AN ERROR TO SHOUT ABOUT. The
      // server now says how long it has been and that a dead one clears itself,
      // so it is shown as information rather than a red failure toast.
      if (body?.code === 'TALLY_SYNC_RUNNING') toast.warning(body.error);
      else toast.danger(body?.error || 'Could not start a sync');
      await load();
    } finally {
      setStarting(false);
    }
  };

  if (loading && !data) return <Card><p className="tally-muted">Loading…</p></Card>;
  if (!data) return null;

  const { machine, queue, recent, runs, connection: conn } = data;
  const canSync = conn.status === 'active' && !data.running;

  return (
    <>
      <Card>
        <div className="tally-head">
          <div>
            <h3 className="tally-section-title">Sync</h3>
            {/* ⚠️ SAID OUT LOUD. Nothing syncs on its own any more, and a user
                who assumes it does will read stale figures as current ones. */}
            <p className="tally-muted">
              Nothing syncs on its own — reading from Tally and writing to it both
              happen only when somebody presses a button here.
            </p>
          </div>
          <div className="tally-actions">
            <Button onClick={syncNow} loading={starting} disabled={starting || !canSync}>
              {data.running ? 'Sync running…' : 'Sync now'}
            </Button>
          </div>
        </div>

        {/* ── The machine, first and separately ── */}
        <div className="tally-status-row">
          <StatusPill
            variant={machine.online ? 'success' : 'danger'}
            label={machine.online ? 'Machine online' : 'Machine not responding'}
          />
          {machine.name ? <span className="tally-muted">{machine.name}</span> : null}
          {machine.lastSeenAt ? (
            <span className="tally-muted">last asked for work {ago(machine.lastSeenAt)}</span>
          ) : null}
          {machine.version ? <span className="tally-muted">agent {machine.version}</span> : null}
        </div>

        {machine.note && (
          <p className={machine.online ? 'tally-muted' : 'tally-warn'}>{machine.note}</p>
        )}

        {conn.status !== 'active' && (
          <p className="tally-warn">This connection is {conn.status} — nothing will run.</p>
        )}

        {/* Verbatim from Tally, per the rule this module keeps: "Tally is not
            running" and "no company is open" are different problems with
            different fixes, and collapsing them into "sync failed" turns a
            self-service correction into a support call. */}
        {conn.lastError && (
          <p className="tally-error">
            <strong>Last attempt failed:</strong> {conn.lastError}
            {conn.consecutiveFailures > 1 && ` (${conn.consecutiveFailures} times in a row)`}
          </p>
        )}

        <p className="tally-muted">
          {conn.lastSuccessfulSyncAt
            ? `Last successful read ${dayTime(conn.lastSuccessfulSyncAt)}.`
            : 'These books have never synced successfully.'}
          {!conn.backfillComplete && ' The first full backfill has not finished yet.'}
        </p>
      </Card>

      <Card>
        <h3 className="tally-section-title">
          {queue.length ? `Queue — ${queue.length} to go` : 'Queue'}
        </h3>
        {!queue.length ? (
          <p className="tally-muted">Nothing waiting. Press Sync now to queue a read.</p>
        ) : (
          <>
            {!machine.online && (
              // The single most useful sentence on this screen: work is queued
              // and the machine that has to do it is not listening.
              <p className="tally-warn">
                These are waiting for a machine that is not responding. They will run
                as soon as it comes back, or be given up on if it does not.
              </p>
            )}
            <Table
              columns={[
                {
                  key: 'what',
                  header: 'What',
                  render: (j) => (
                    <>
                      {j.label}
                      {/* A row that will change a client's books must be
                          distinguishable at a glance from the ones that read. */}
                      {j.writes ? <StatusPill variant="warning" label="writes" /> : null}
                    </>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (j) => {
                    const p = JOB_PILL[j.status] || { variant: 'default', label: j.status };
                    return <StatusPill variant={p.variant} label={p.label} />;
                  },
                },
                {
                  key: 'tries',
                  header: 'Tries',
                  render: (j) => (j.maxAttempts > 1 ? `${j.attempts} of ${j.maxAttempts}` : 'one only'),
                },
                { key: 'when', header: 'Waiting', render: (j) => ago(j.queuedAt) },
              ]}
              data={queue}
              keyField="id"
            />
          </>
        )}
      </Card>

      <Card>
        <h3 className="tally-section-title">Recently finished</h3>
        {!recent.length ? (
          <p className="tally-muted">Nothing has run for this company yet.</p>
        ) : (
          <Table
            columns={[
              {
                key: 'what',
                header: 'What',
                render: (j) => (
                  <>
                    {j.label}
                    {j.writes ? <StatusPill variant="warning" label="writes" /> : null}
                  </>
                ),
              },
              {
                key: 'status',
                header: 'Result',
                render: (j) => {
                  const p = JOB_PILL[j.status] || { variant: 'default', label: j.status };
                  return <StatusPill variant={p.variant} label={p.label} />;
                },
              },
              {
                key: 'detail',
                header: 'Detail',
                // ⚠️ THE ERROR IS SHOWN IN FULL, not truncated to a tooltip. It
                // is the whole reason somebody opened this tab, and it is
                // written to be acted on ("no company is open").
                render: (j) => {
                  if (j.error) return <span className="tally-error">{j.error}</span>;
                  if (j.receipt) {
                    return (
                      <span className="tally-muted">
                        {j.receipt.created} created, {j.receipt.combined} matched,
                        {' '}{j.receipt.altered} altered
                      </span>
                    );
                  }
                  return <span className="tally-muted">—</span>;
                },
              },
              { key: 'when', header: 'Finished', render: (j) => ago(j.finishedAt) || '—' },
            ]}
            data={recent}
            keyField="id"
          />
        )}
      </Card>

      <Card>
        <h3 className="tally-section-title">Sync history</h3>
        {!runs.length ? (
          <p className="tally-muted">No sync has been run for this company yet.</p>
        ) : (
          <Table
            columns={[
              { key: 'when', header: 'Started', render: (r) => dayTime(r.startedAt) },
              {
                key: 'by',
                header: 'Started by',
                // 'schedule' rows are history from before syncing became manual;
                // labelling them plainly is better than hiding that it changed.
                render: (r) => (r.trigger === 'manual' ? 'A person'
                  : r.trigger === 'schedule' ? 'Automatic (no longer used)'
                    : r.trigger),
              },
              {
                key: 'status',
                header: 'Result',
                render: (r) => {
                  const p = RUN_PILL[r.status] || { variant: 'default', label: r.status };
                  return <StatusPill variant={p.variant} label={p.label} />;
                },
              },
              {
                key: 'counts',
                header: 'Read',
                render: (r) => (
                  <span className="tally-muted">
                    {r.counts?.ledgersWritten || 0} ledgers, {r.counts?.vouchersWritten || 0} entries
                  </span>
                ),
              },
              {
                key: 'why',
                header: 'Failures',
                render: (r) => (r.failures?.length
                  ? <span className="tally-error">{r.failures.join('; ')}</span>
                  : <span className="tally-muted">—</span>),
              },
            ]}
            data={runs}
            keyField="id"
          />
        )}
      </Card>
    </>
  );
}

export default TallySyncActivity;
